/**
 * Customer-facing title for a menu item (family/student ordering surfaces).
 *
 * The catalog distinguishes the internal `name` ("Milk, Plain, 1% Lowfat,
 * Carton (8 oz)") from the optional family-facing `displayName` ("1% Milk").
 * Menu items ALSO carry their own `name`, but the template sync stamps it
 * with the component/bundle CATALOG name (`canonicalName` in the menus
 * builder) and the rename cascade keeps it in step — so a menu-item name
 * that simply equals the catalog name is a sync artifact, not an authored
 * retitle, and the display label must win over it. Only a name that DIFFERS
 * from the catalog name is a deliberate per-item override and is kept.
 *
 * ## Localization (PROD-1293)
 *
 * Both resolvers take an OPTIONAL trailing `MenuStringDictionary`. Omit it and
 * the output is byte-identical to the pre-localization behavior — that is what
 * keeps the 47 existing call sites and every golden fixture untouched.
 *
 * Titles are localized at the ATOM, not the composite. 549 of the 867 distinct
 * live display strings are ` • `-joined composites of component labels
 * (measured 2026-09-15), and those 867 composites are built from only 395
 * distinct atoms. Translating atoms means "Chicken Tenders" is reviewed once
 * and reads identically on every menu, and a newly generated combination of
 * known atoms needs no new translation at all. A whole-composite entry still
 * wins when one exists, so a hand-tuned full line is always possible without a
 * schema change.
 *
 * DISPLAY-ONLY. A localized title must never reach a shipment line, a
 * production record, or an order confirmation, or the same order renders
 * differently depending on who printed it. Those call sites pass no dictionary.
 */

/**
 * Separator between component-kind groups in a composed bundle title.
 * `buildBundleAutoTitle` joins with this; `translateMenuTitle` splits on it.
 */
export const MENU_TITLE_SEPARATOR = ' • '

/**
 * Approved translations for one locale, keyed on the source English string.
 *
 * Built by the caller (`MenuTranslationManager.loadApprovedDictionary`) and
 * merely READ here — the resolvers stay pure and synchronous so one function
 * serves both the `javaScriptEnabled: false` PDF renderer and the React
 * client. Never make them do I/O.
 */
export type MenuStringDictionary = Readonly<Record<string, string>>

interface TitledCatalogRef {
  name?: string | null
  displayName?: string | null
}

function trimmed(value: string | null | undefined): string | null {
  const v = value?.trim()
  return v && v.length > 0 ? v : null
}

function lookup(text: string, dictionary: MenuStringDictionary): string | null {
  const key = text.trim()
  return trimmed(dictionary[key]) ?? trimmed(dictionary[key.toLowerCase()])
}

/**
 * Localize a menu title, whole-composite first then atom-by-atom.
 *
 * Falls back per SEGMENT: an untranslated atom keeps its English text rather
 * than dropping out, so a partly-authored corpus yields a partly-Spanish line
 * and never a blank one. Returns the input unchanged when there is no
 * dictionary or nothing in it matches.
 */
export function translateMenuTitle(title: string, dictionary?: MenuStringDictionary | null): string {
  if (!dictionary) {
    return title
  }
  const whole = lookup(title, dictionary)
  if (whole) {
    return whole
  }
  if (!title.includes(MENU_TITLE_SEPARATOR)) {
    return title
  }
  return title
    .split(MENU_TITLE_SEPARATOR)
    .map((atom) => lookup(atom, dictionary) ?? atom)
    .join(MENU_TITLE_SEPARATOR)
}

export function resolveMenuItemTitle(
  item: {
    name?: string | null
    bundle?: TitledCatalogRef | null
    component?: TitledCatalogRef | null
  },
  dictionary?: MenuStringDictionary | null,
): string | null {
  const override = trimmed(item.name)
  const catalog = item.bundle ?? item.component ?? null
  const catalogName = trimmed(catalog?.name)
  const displayLabel = trimmed(catalog?.displayName)
  // A deliberate per-item retitle (differs from the catalog name) always wins.
  const resolved = override && override !== catalogName ? override : (displayLabel ?? catalogName ?? override)
  if (resolved === null) {
    return null
  }
  return translateMenuTitle(resolved, dictionary)
}
